import broadcastManager from "./helper/broadcastManager";
import { computed, createApp } from "vue";
import App from "./App.vue";

import router from "./router";

import Pinia from "./pinia";

import AuthenticationHelper from "./helper/OIDCManager";
import buildVersion from "./buildversion.json";
import { reloadOnceAfterDeploy, clearDeployReloadGuard, isChunkLoadError } from "./helper/deployReload";

// Récupération auto après déploiement (Mantis 0028073) : si le préchargement d'un chunk
// dynamique échoue (fichier hashé absent du serveur après une mise en ligne), on recharge
// une seule fois pour récupérer un index.html cohérent — au lieu de rester sur une page blanche.
window.addEventListener("vite:preloadError", (event: Event) => {
  event.preventDefault();
  reloadOnceAfterDeploy("vite:preloadError");
});
window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
  if (isChunkLoadError(event.reason)) {
    reloadOnceAfterDeploy("unhandledrejection:chunk");
  }
});
import frStartup from "./i18n/locales/fr/startup.json";
import enStartup from "./i18n/locales/en/startup.json";
import deStartup from "./i18n/locales/de/startup.json";
import esStartup from "./i18n/locales/es/startup.json";
import itStartup from "./i18n/locales/it/startup.json";

// Injection immédiate dans l'écran de chargement (synchrone, avant tout await)
const _startupLocales: Record<string, typeof frStartup.startup> = {
  fr: frStartup.startup, en: enStartup.startup,
  de: deStartup.startup, es: esStartup.startup, it: itStartup.startup,
};
const _browserLang = (navigator.language || 'fr').substring(0, 2).toLowerCase();
const _startupT = _startupLocales[_browserLang] || frStartup.startup;

if (typeof (window as any).__o2sSetLabels === 'function') {
  (window as any).__o2sSetLabels([_startupT.step1, _startupT.step2, _startupT.step3, _startupT.step4, _startupT.step5]);
}
if (typeof (window as any).__o2sSetVersion === 'function') {
  const version = `v${buildVersion.buildMajor}.${buildVersion.buildMinor}.${buildVersion.buildRevision}`;
  const date = (buildVersion.buildDeployedDateTime || buildVersion.buildDateFormat || '').substring(0, 10);
  (window as any).__o2sSetVersion(version, date);
}

// Initialisation du UserManager OIDC (startSilentRenew, événements)
const auth = new AuthenticationHelper();

// Forcer un refresh token au démarrage pour garantir des claims à jour
const user = await AuthenticationHelper._userManager.getUser();

console.log("User au démarrage:", user);

if (user?.refresh_token) {
  try {
    console.log(AuthenticationHelper._userManager.settings.scope);
    await AuthenticationHelper._userManager.signinSilent();
    console.log("Refresh token au démarrage réussi");
  } catch (error) {
    console.error("Refresh token au démarrage échoué:", error);
  }
}

import ElementPlus from "element-plus";
import "element-plus/dist/index.css";

import VueKonva from 'vue-konva';

import frFR from 'element-plus/es/locale/lang/fr';
import enEN from 'element-plus/es/locale/lang/en';

import i18n from "./i18n";
import { abilitiesPlugin } from '@casl/vue';
import { fetchUserRights } from "./services/Ability";

import '@/assets/css/style.css';
// @ts-ignore
import FlagIcon from "vue-flag-icon";
import { initializeCommonStores, setLoadingStep, setLoadingStepError } from "./helper/storeLoader";
import { KeyNavigate } from './directives/key-navigate'

const lang = computed(() => {
  switch (i18n.global.locale) {
    case "fr":
      return frFR;
    case "en":
      return enEN;
    default:
      return frFR;
  }
})

const app = createApp(App)
  .use(Pinia);

// Vérification post-signinSilent : l'utilisateur est-il authentifié ?
const authenticatedUser = await AuthenticationHelper._userManager.getUser();

let storesHaveErrors = false;
let rightsHaveErrors = false;

if (authenticatedUser) {
  // Utilisateur connecté → afficher la progression puis initialiser les stores
  if (typeof (window as any).__o2sShowProgress === 'function') {
    (window as any).__o2sShowProgress();
  }
  const { hasErrors } = await initializeCommonStores();
  storesHaveErrors = hasErrors;
}

setLoadingStep(5);
let userRights;
try {
  userRights = await fetchUserRights();
} catch (e) {
  console.error('❌ Phase 5 (droits) failed', e);
  setLoadingStepError(5);
  rightsHaveErrors = true;
  userRights = [];
}
app
  .use(abilitiesPlugin, userRights, {
    useGlobalProperties: true
  })
  .use(router)
  .use(i18n)
  .use(FlagIcon)
  .use(VueKonva)
  .use(ElementPlus, { locale: lang.value })
  .use(KeyNavigate)
  //.use(VueSweetalert2)
  .mount("#app");

// Démarrage réussi : on lève la garde pour qu'un futur déploiement puisse de nouveau
// déclencher un rechargement de récupération si besoin (Mantis 0028073).
clearDeployReloadGuard();

// Retrait de l'écran de chargement
const startupEl = document.getElementById('o2s-startup');
if (startupEl) {
  if (authenticatedUser) {
    // Connecté : 100% puis fondu
    setLoadingStep(6);
    setTimeout(() => {
      startupEl.classList.add('o2s-fade-out');
      setTimeout(() => startupEl.remove(), 380);
    }, 300);
  } else {
    // Non connecté : suppression immédiate (le router redirigera vers le login)
    startupEl.remove();
  }
}

// Redirection vers la page d'erreur si le chargement a échoué
if (storesHaveErrors || rightsHaveErrors) {
  router.push({ name: 'StartupError' });
}

broadcastManager();